/**
 * Reprise d'un ancien logiciel : lecture des fichiers CSV et modèles.
 *
 * <p>Le navigateur découpe le fichier en lignes, colonne par colonne ; le
 * serveur seul juge de ce qu'elles valent (aperçu puis import, tout ou rien).
 * Un classeur Excel s'enregistre au format CSV (« Fichier → Enregistrer sous →
 * CSV ») : le séparateur — point-virgule, virgule ou tabulation — est deviné.
 */

/** Types repris, dans l'ordre conseillé : les clients d'abord, les pièces les nomment. */
export const TYPES_REPRISE = [
  {
    id: 'clients',
    libelle: '👥 Clients',
    colonnes: [
      'Nom',
      'Contact',
      'Adresse',
      'NPA Ville',
      'Téléphone',
      'Mobile',
      'Email',
      'Numéro IDE',
      'Notes',
      'Client depuis',
    ],
    exemple: [
      'Dupont Rénovation Sàrl',
      'Marc Dupont',
      'Rue du Lac 12',
      '1003 Lausanne',
      '021 555 12 34',
      '079 555 12 34',
      'marc@dupont.ch',
      'CHE-123.456.789',
      'Client fidèle',
      '14.02.2011',
    ],
  },
  {
    id: 'devis',
    libelle: '📝 Devis',
    colonnes: ['Numéro', 'Date', 'Client', 'Libellé', 'Montant HT', 'Taux TVA', 'Statut'],
    exemple: [
      'D-2019-042',
      '30.06.2019',
      'Dupont Rénovation Sàrl',
      'Rénovation cuisine',
      '4500.00',
      '7.7',
      'Accepté',
    ],
  },
  {
    id: 'factures',
    libelle: '🧾 Factures',
    colonnes: [
      'Numéro',
      'Date',
      'Client',
      'Libellé',
      'Montant HT',
      'Taux TVA',
      'Statut',
      'Date paiement',
    ],
    exemple: [
      '2019-108',
      '15.11.2019',
      'Dupont Rénovation Sàrl',
      'Rénovation cuisine',
      '4500.00',
      '7.7',
      'Payée',
      '10.12.2019',
    ],
  },
  {
    id: 'depenses',
    libelle: '💸 Dépenses',
    colonnes: ['Date', 'Libellé', 'Montant TTC', 'Taux TVA', 'Catégorie', 'Fournisseur', 'Compte'],
    exemple: ['03.05.2019', 'Ciment et sable', '1234.50', '7.7', 'Matériel', 'Gétaz', '4000'],
  },
];

/** Ce que chaque type admet, rappelé sous le choix. */
export const AIDE_REPRISE = {
  clients: 'Un client déjà présent (même nom) est ignoré, jamais dédoublé.',
  devis:
    'Devis clos seulement : accepté, refusé ou expiré. Le numéro est gardé, précédé de « REP- ».',
  factures:
    'Factures closes seulement : payée ou annulée. Le numéro est gardé, précédé de « REP- » : il ne touche pas à la numérotation de tes nouvelles factures, et aucune QR-facture n’est ajoutée. Une facture encore ouverte se ressaisit comme nouvelle facture.',
  depenses:
    'Montant TVA comprise ; le compte, s’il est donné, doit exister dans ton plan comptable.',
};

/** Échappe une valeur pour le CSV (point-virgule, guillemets, retours à la ligne). */
const champCsv = (v) => {
  const s = String(v ?? '');
  return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

/**
 * Modèle CSV d'un type : en-têtes et une ligne d'exemple, au point-virgule
 * (ce qu'Excel suisse ouvre directement), précédé d'une marque UTF-8 pour que
 * les accents s'affichent.
 *
 * @param {string} type identifiant du type
 * @returns {string} le contenu du fichier
 */
export function modeleCsv(type) {
  const t = TYPES_REPRISE.find((x) => x.id === type);
  if (!t) return '';
  return '\uFEFF' + [t.colonnes, t.exemple].map((l) => l.map(champCsv).join(';')).join('\r\n');
}

/** Devine le séparateur d'après la ligne d'en-tête. */
function separateur(entete) {
  const compte = (c) => entete.split(c).length - 1;
  return ['\t', ';', ','].reduce(
    (meilleur, c) => (compte(c) > compte(meilleur) ? c : meilleur),
    ';',
  );
}

/**
 * Découpe un texte CSV en lignes de valeurs (guillemets et retours à la ligne
 * entre guillemets compris).
 *
 * @param {string} texte contenu du fichier
 * @param {string} sep séparateur
 * @returns {string[][]} les lignes
 */
function decoupe(texte, sep) {
  const lignes = [];
  let ligne = [];
  let champ = '';
  let entreGuillemets = false;
  for (let i = 0; i < texte.length; i += 1) {
    const c = texte[i];
    if (entreGuillemets) {
      if (c === '"' && texte[i + 1] === '"') {
        champ += '"';
        i += 1;
      } else if (c === '"') {
        entreGuillemets = false;
      } else {
        champ += c;
      }
    } else if (c === '"') {
      entreGuillemets = true;
    } else if (c === sep) {
      ligne.push(champ);
      champ = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && texte[i + 1] === '\n') i += 1;
      ligne.push(champ);
      lignes.push(ligne);
      ligne = [];
      champ = '';
    } else {
      champ += c;
    }
  }
  if (champ !== '' || ligne.length) {
    ligne.push(champ);
    lignes.push(ligne);
  }
  return lignes;
}

/**
 * Lit un fichier CSV exporté d'un autre logiciel.
 *
 * @param {string} texte contenu du fichier
 * @returns {{colonnes: string[], lignes: Object<string, string>[]}} l'en-tête et
 *   les lignes non vides, chacune « en-tête → valeur »
 */
export function lisCsv(texte) {
  const propre = String(texte || '').replace(/^\uFEFF/, '');
  const premiere = propre.split(/\r?\n/, 1)[0] || '';
  const toutes = decoupe(propre, separateur(premiere));
  const [entete = [], ...reste] = toutes;
  const colonnes = entete.map((c) => c.trim());
  const lignes = reste
    .filter((l) => l.some((v) => v.trim() !== ''))
    .map((l) => Object.fromEntries(colonnes.map((c, i) => [c, (l[i] ?? '').trim()])));
  return { colonnes, lignes };
}
